import React from "react";
import { useTranslation } from "react-i18next";

interface FooterProps {
  appleButtonSrc: string;
  googleButtonSrc: string;
}

/**
 * Footer component
 *
 * Displays the footer section with links, contact information, and app download buttons.
 * Uses i18n for translations.
 *
 * @param {string} appleButtonSrc - Path to the App Store button image
 * @param {string} googleButtonSrc - Path to the Google Play button image
 * @returns {JSX.Element} The footer component
 */
export function Footer({ appleButtonSrc, googleButtonSrc }: FooterProps) {
  const { t } = useTranslation();
  return (
    <footer className="w-full bg-[#3E4088] text-white py-12 px-6 mt-20">
      <div className="max-w-6xl mx-auto grid gap-10 md:grid-cols-4">
        {/* Coluna 1 - Sobre / logo / descrição / copyright */}
        <div className="space-y-4 md:col-span-1">
          <a href="https://www.walrus.com" className="inline-block">
            <img
              src="/images/walrus-logo.png"
              alt={t("common.logoAlt")}
              className="h-6 w-auto object-contain"
            />
          </a>

          <p className="text-white text-base font-semibold">
            {t("footer.tagline")}
          </p>

          <p className="text-xs text-[#E9EBFF]/90 max-w-md font-medium leading-relaxed">
            {t("footer.description")}
          </p>

          <p className="text-sm text-[#E9EBFF]/70 font-medium">
            {t("footer.copyright")}
          </p>
        </div>

        {/* Coluna 2 - Resources */}
        <div className="text-sm space-y-3">
          <h3 className="font-semibold text-white text-sm tracking-tight">
            {t("footer.resources.title")}
          </h3>
          <nav className="space-y-2 text-[#AFB5E5] font-medium">
            <a
              href="https://walrus.com/swell"
              target="_blank"
              rel="noopener"
              className="block hover:text-white transition-colors"
            >
              {t("footer.resources.swell")}
            </a>
            <a
              href="https://walrus.com/questions"
              target="_blank"
              rel="noopener"
              className="block hover:text-white transition-colors"
            >
              {t("footer.resources.questions")}
            </a>
            <a
              href="https://walrus.com/articles"
              target="_blank"
              rel="noopener"
              className="block hover:text-white transition-colors"
            >
              {t("footer.resources.articles")}
            </a>
            <a
              href="https://walrus.com/drugs"
              target="_blank"
              rel="noopener"
              className="block hover:text-white transition-colors"
            >
              {t("footer.resources.drugs")}
            </a>
            <a
              href="https://walrus.com/ask-question"
              target="_blank"
              rel="noopener"
              className="block hover:text-white transition-colors"
            >
              {t("footer.resources.askQuestion")}
            </a>
            <a
              href="/careers"
              className="block hover:text-white transition-colors"
            >
              {t("footer.resources.careers")}
            </a>
            <a
              href="/eula"
              className="block hover:text-white transition-colors"
            >
              {t("footer.resources.terms")}
            </a>
            <a
              href="/privacy"
              className="block hover:text-white transition-colors"
            >
              {t("footer.resources.privacy")}
            </a>
          </nav>
        </div>

        {/* Coluna 3 - Accounts + Employers + Support (texto) */}
        <div className="text-sm space-y-6">
          <div>
            <h3 className="font-semibold text-white text-sm tracking-tight">
              {t("footer.individuals.title")}
            </h3>
            <div className="mt-2 space-y-2 text-[#AFB5E5] font-medium">
              <a
                href="https://member.walrus.com/"
                target="_blank"
                rel="noopener"
                className="block hover:text-white transition-colors"
              >
                {t("footer.individuals.createAccount")}
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-white text-sm tracking-tight">
              {t("footer.employers.title")}
            </h3>
            <div className="mt-2 space-y-2 text-[#AFB5E5] font-medium">
              <a
                href="https://walrus.com/sales"
                rel="noopener"
                className="block hover:text-white transition-colors"
              >
                {t("footer.employers.requestInfo")}
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-white text-sm tracking-tight">
              {t("footer.support.title")}
            </h3>
            <p className="mt-2 text-[#AFB5E5] font-medium">
              {t("footer.support.contactSupport")}
            </p>

            <div className="mt-3 space-y-2 text-[#AFB5E5] font-medium">
              <a
                href="tel:1855-492-5787"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <span className="inline-flex items-center justify-center ">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#afb5e5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </span>
                {t("footer.support.phone")}
              </a>

              <a
                href="mailto:hello@walrus.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <span className="inline-flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#afb5e5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </svg>
                </span>
                {t("footer.support.email")}
              </a>
            </div>
          </div>
        </div>

        {/* Coluna 4 - App badges + texto */}
        <div className="space-y-4">
          <div className="space-y-3 md:space-x-0 sm:space-x-3 ">
            <a
              href="https://apple.co/48hqxkB"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img
                src={appleButtonSrc}
                alt={t("footer.download.appStore")}
                className="h-11 w-auto"
              />
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.walrus.member"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img
                src={googleButtonSrc}
                alt={t("footer.download.googlePlay")}
                className="h-11 w-auto"
              />
            </a>
          </div>

          <p
            className="text-xs text-[#AFB5E5] font-medium leading-snug"
            dangerouslySetInnerHTML={{ __html: t("footer.download.watchText") }}
          />
        </div>
      </div>
    </footer>
  );
}
